import { useState, useEffect, useContext } from 'react';
import {
    Box,
    Typography,
    useMediaQuery,
    useTheme,
    Container,
} from '@mui/material';
import { parseISO, isAfter, isBefore } from 'date-fns';
import { Link, useParams } from 'react-router-dom';
import ModalImage from 'react-modal-image';
import { useTranslation } from 'react-i18next';
import { AppContext, SttTabs } from '../../../sporttia/all';
import { useCrud, useLoader } from '../../../lib/hooks';
import { formatPeriod } from '../../../lib/utils';
import { getPath } from '../../Pages';
import EventDetails from './EventDetails';
import EventInscriptions from './EventInscriptions';
import EventTeam from './EventTeam';
import SignedUsersList from '../../../sporttia/lists/SignedUsersList';
import constants from '../../../config/constants';
import SignedTeamsByCategoryList from '../../../sporttia/lists/SignedTeamsByCategoryList';

export default function Event() {
    const { id } = useParams();
    const [event, setEvent, , Get] = useCrud<any>();
    const [inscriptions, setInscriptions] = useState<any[]>([]);
    const [team, setTeam] = useState<any>();
    const [me, setMe] = useState<any>();
    const [eventCategories, setEventCategories] = useState<any>();
    const [files, setFiles] = useState<any[]>([]);
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [signedParticipants, setSignedParticipants] = useState<any>();

    const theme = useTheme();
    const mdUp = useMediaQuery(theme.breakpoints.up('md'));
    const smDown = useMediaQuery(theme.breakpoints.down('sm'));
    const [, loader] = useLoader([event ? 'success' : 'loading']);
    const showSignedParticipants =
        event?.showParticipants &&
        (signedParticipants?.length > 0 || event?.numTeams > 0);
    function loadEvent() {
        Get(`/events/${id}`)
            .then((response: any) => {
                if (response.event) {
                    setEvent(response.event);
                    setInscriptions(response.inscriptions);
                    setTeam(response.team);
                    setEventCategories(response.event.categories);
                }

                if (response.me) {
                    setMe(response.me);
                }

                if (response?.signedParticipants) {
                    const participants = response?.signedParticipants?.rows
                        ?.map((participant: any) => ({
                            user: {
                                fullName: participant?.fullName,
                            },
                        }))
                        .flat();
                    setSignedParticipants(participants);
                }
            })
            .catch();
    }

    function loadFiles() {
        Get(`/events/${id}/files`).then((response: any) => {
            if (response.count > 0) {
                setFiles(response.rows);
            }
        });
    }

    useEffect(() => {
        if (id) {
            loadEvent();
            loadFiles();
        }
    }, []);

    if (!event) {
        return loader;
    }

    const now = new Date();
    const inTime =
        isAfter(now, parseISO(event.ini)) && isBefore(now, parseISO(event.end));
    const inIscTime =
        isAfter(now, parseISO(event.inscIni)) &&
        isBefore(now, parseISO(event.inscEnd));
    const dateFormat = mdUp ? 'D MMMM YYYY' : 'DD/MM/YYYY';
    const timeFormat = 'HH:mm:ss';
    const [period, ,] = formatPeriod(event.ini, event.end, { dateFormat }, t);
    const [, , , , dateTime] = formatPeriod(
        event.inscIni,
        event.inscEnd,
        { dateFormat, timeFormat },
        t,
    );

    return (
        <Container maxWidth="md">
            <Box display="flex" flexDirection={smDown ? 'column' : 'row'}>
                <Box>
                    <Box position="relative">
                        {event.poster.id ? (
                            <ModalImage
                                hideZoom
                                small={`${event.poster.url}?outfit=282`}
                                large={`${event.poster.url}?outfit=800`}
                                alt={event.name}
                            />
                        ) : (
                            <img
                                style={{
                                    width: smDown ? '100%' : '282px',
                                    height: 'auto',
                                    borderRadius: 4,
                                }}
                                src={`${cxt.apiEnv.url}/v6/public/images/eventPosters/event_default_68.jpg`}
                                alt=""
                            />
                        )}
                    </Box>
                    <Box
                        sx={{
                            p: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            backgroundColor: inTime ? '#4caf50' : '#f44336',
                            color: 'white',
                            borderRadius: 1,
                            mt: 2,
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{ fontWeight: 'bold' }}
                        >
                            {inTime
                                ? t('page.event.inProcess')
                                : t('page.event.notInProcess')}
                        </Typography>
                        <Typography variant="body1" style={{ fontSize: 16 }}>
                            {period}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            p: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            backgroundColor: inIscTime ? '#4caf50' : '#f44336',
                            color: 'white',
                            borderRadius: 1,
                            mt: 2,
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{ fontWeight: 'bold' }}
                        >
                            {inIscTime
                                ? t('page.event.inscInTime')
                                : t('page.activity.inscOutTime')}
                        </Typography>
                        <Typography variant="body1" style={{ fontSize: 16 }}>
                            {`${dateTime}`}
                        </Typography>
                    </Box>
                    {files.length > 0 && (
                        <Box mb={3}>
                            <Box mt={3} mb={2}>
                                <Typography variant="body1">
                                    <b>{t('labels.documents')}:</b>
                                </Typography>
                            </Box>
                            {files.map((file: any) => (
                                <Box my={1} key={file?.url}>
                                    <div>
                                        <a href={file.url}>{file.name}</a>
                                    </div>
                                    <Typography variant="body2">
                                        {file.description}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
                <Box p={mdUp ? 2 : 0} flex={1}>
                    <Box mb={2}>
                        <Typography variant="h5">{event.name}</Typography>
                        <Typography variant="body1">
                            <Link
                                to={getPath('scProfile', { id: event.sc.id })}
                            >
                                {event.sc.short}
                            </Link>
                        </Typography>
                        {me && (
                            <Box
                                my={1}
                                sx={{
                                    backgroundColor: '#e3f2fd',
                                    color: '#1976d2',
                                    padding: 1,
                                    borderRadius: 1,
                                }}
                            >
                                <Typography>
                                    {t('page.event.ImSigned')}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    <SttTabs
                        tabs={[
                            {
                                caption: t('labels.event'),
                                component: (
                                    <EventDetails
                                        event={event}
                                        team={team}
                                        inscriptionDisabled={!inIscTime}
                                        onInscription={loadEvent}
                                    />
                                ),
                            },
                            {
                                caption: t('event.inscriptions'),
                                component: (
                                    <EventInscriptions
                                        event={event}
                                        inscriptions={inscriptions}
                                    />
                                ),
                                show: inscriptions?.length > 0,
                            },
                            {
                                caption: t('event.team'),
                                component: (
                                    <EventTeam
                                        event={event}
                                        team={team}
                                        onDelete={loadEvent}
                                    />
                                ),
                                show: !!team,
                            },
                            {
                                caption: t('generic.signedUsers'),
                                show: showSignedParticipants,
                                component:
                                    event?.type ===
                                    constants.events.types.tournament ? (
                                        <SignedTeamsByCategoryList
                                            items={eventCategories}
                                        />
                                    ) : (
                                        <SignedUsersList
                                            items={signedParticipants}
                                        />
                                    ),
                            },
                        ]}
                    />
                </Box>
            </Box>
        </Container>
    );
}
